/**
 * Vigia de inatividade.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 4: 30 minutos parado encerra a sessao, e
 * aos 25 minutos o usuario e avisado, com opcao de continuar.
 *
 * Quem manda na inatividade e a API, que mede em `sessoes_usuario` a cada
 * requisicao. Este vigia e a cortesia do lado da tela: avisa antes, e da o botao
 * que renova. Se ele errar por alguns segundos, a API ainda cobra — nao ha como
 * ficar conectado por engano.
 *
 * ATIVIDADE aqui e o que poderia virar requisicao: clique, tecla, rolagem,
 * toque. Movimento de mouse de proposito NAO conta — quem le uma tela longa
 * mexendo o mouse esta, para a API, tao parado quanto quem saiu para o almoco, e
 * o aviso ficaria mentindo.
 */

export const MINUTOS_ATE_AVISO = 25;
export const MINUTOS_ATE_ENCERRAR = 30;

/** Eventos que contam como uso do sistema. */
const EVENTOS: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

/** De quanto em quanto tempo o vigia confere o relogio. */
const INTERVALO_CONFERENCIA_MS = 5_000;

export interface OpcoesVigia {
  aoAvisar: (encerraEm: number) => void;
  aoEncerrar: () => void;
  /** Chamado quando houve atividade e o aviso ja estava na tela. */
  aoRetomar?: () => void;
  minutosAviso?: number;
  minutosEncerrar?: number;
  agora?: () => number;
}

export interface VigiaDeInatividade {
  iniciar: () => void;
  parar: () => void;
  /** Zera a contagem. Usado por "continuar conectado" e por atividade real. */
  reiniciar: () => void;
  /** Para teste: avanca a conferencia sem esperar o intervalo. */
  conferir: () => void;
}

export function criarVigiaDeInatividade(opcoes: OpcoesVigia): VigiaDeInatividade {
  const agora = opcoes.agora ?? (() => Date.now());
  const msAviso = (opcoes.minutosAviso ?? MINUTOS_ATE_AVISO) * 60_000;
  const msEncerrar = (opcoes.minutosEncerrar ?? MINUTOS_ATE_ENCERRAR) * 60_000;

  let ultimaAtividade = agora();
  let avisando = false;
  let encerrado = false;
  let temporizador: ReturnType<typeof setInterval> | null = null;

  function conferir(): void {
    if (encerrado) {
      return;
    }

    const parado = agora() - ultimaAtividade;

    if (parado >= msEncerrar) {
      encerrado = true;
      parar();
      opcoes.aoEncerrar();
      return;
    }

    if (parado >= msAviso && !avisando) {
      avisando = true;
      opcoes.aoAvisar(ultimaAtividade + msEncerrar);
    }
  }

  function registrarAtividade(): void {
    if (encerrado) {
      return;
    }

    // Enquanto o aviso esta na tela, clicar em qualquer lugar NAO estende a
    // sessao: a pessoa tem de dizer que quer continuar. Do contrario, mover-se
    // para ler o aviso ja o desfaria, e o aviso viraria enfeite.
    if (avisando) {
      return;
    }

    ultimaAtividade = agora();
  }

  function reiniciar(): void {
    if (encerrado) {
      return;
    }
    const estavaAvisando = avisando;
    avisando = false;
    ultimaAtividade = agora();
    if (estavaAvisando) {
      opcoes.aoRetomar?.();
    }
  }

  function iniciar(): void {
    if (temporizador) {
      return;
    }
    ultimaAtividade = agora();
    avisando = false;
    encerrado = false;

    for (const evento of EVENTOS) {
      window.addEventListener(evento, registrarAtividade, { passive: true });
    }
    /**
     * Voltar para a aba conta como atividade? Nao — quem volta depois de 40
     * minutos tem de entrar de novo, e a API vai cobrar isso de todo jeito. Mas
     * a conferencia roda na hora, porque temporizador de aba escondida e
     * estrangulado pelo navegador e pode ter perdido o momento.
     */
    document.addEventListener('visibilitychange', conferir);

    temporizador = setInterval(conferir, INTERVALO_CONFERENCIA_MS);
  }

  function parar(): void {
    if (temporizador) {
      clearInterval(temporizador);
      temporizador = null;
    }
    for (const evento of EVENTOS) {
      window.removeEventListener(evento, registrarAtividade);
    }
    document.removeEventListener('visibilitychange', conferir);
  }

  return { iniciar, parar, reiniciar, conferir };
}
